<template>
  <div class="card-body row" v-loading.lock="remoteLoading">
    <div class="col-md-6 col-sm-12">
      <el-select
        class="select-default"
        v-model="pagination.perPage"
        :label="'Per page' | translate">
        <el-option
          class="select-default"
          v-for="item in pagination.perPageOptions"
          :key="item"
          :label="item"
          :value="item">
        </el-option>
      </el-select>
      <div class="paginated-table-buttons pull-right">
        <slot name="buttons"></slot>
      </div>
    </div>
    <div class="col-md-6 col-sm-12" v-if="searchEnabled">
      <fg-input
        :placeholder="'Search' | translate"
        @input="debounceSearchQuery" aria-controls="datatables" :value="searchQuery"
        addon-right-icon="nc-icon nc-zoom-split">
      </fg-input>
    </div>
    <div class="col-sm-12 mt-2">
      <slot v-bind:queriedData="queriedData" v-bind:sortChange="sortChange">
        <el-table class="table-striped"
                  ref="elTable"
                  :class="classes"
                  :data="queriedData"
                  @sort-change="sortChange"
                  border
                  style="width: 100%"
                  :filter-method="filterHandler"
                  @select="(selection, row) => emitEventHandler('select', selection, row)"
                  @select-all="selection => emitEventHandler('select-all', selection)"
                  @selection-change="selection => emitEventHandler('selection-change', selection)"
                  @cell-mouse-enter="(row, column, cell, event) => emitEventHandler('cell-mouse-enter', row, column, cell, event)"
                  @cell-mouse-leave="(row, column, cell, event) => emitEventHandler('cell-mouse-leave', row, column, cell, event)"
                  @cell-click="(row, column, cell, event) => emitEventHandler('cell-click', row, column, cell, event)"
                  @cell-dblclick="(row, column, cell, event) => emitEventHandler('cell-dblclick', row, column, cell, event)"
                  @row-click="(row, event, column) => emitEventHandler('row-click', row, event, column)"
                  @row-dblclick="(row, event) => emitEventHandler('row-dblclick', row, event)"
                  @row-contextmenu="(row, event) => emitEventHandler('row-contextmenu', row, event)"
                  @header-click="(column, event) => emitEventHandler('header-click', column, event)"
                  @filter-change="filterHandler"
                  @current-change="(currentRow, oldCurrentRow) => emitEventHandler('current-change', currentRow, oldCurrentRow)"
                  @header-dragend="(newWidth, oldWidth, column, event) => emitEventHandler('header-dragend', newWidth, oldWidth, column, event)"
                  @expand-change="(row, expanded) => emitEventHandler('expand-change', row, expanded)">
          <slot name="tableColumns">
            <el-table-column v-for="column in tableColumns"
                             :key="column.label"
                             :min-width="column.minWidth"
                             :prop="column.prop"
                             :label="column.label">
            </el-table-column>
          </slot>
        </el-table>
      </slot>
    </div>
    <div class="col-sm-6 pagination-info">
      <p class="category" v-translate="{from: from + 1, to, total: pagination.total}">Showing %{ from } to %{ to } of %{ total } entries</p>
    </div>
    <div class="col-sm-6">
      <p-pagination class="pull-right"
                    v-model="pagination.currentPage"
                    :per-page="pagination.perPage"
                    :total="pagination.total">
      </p-pagination>
    </div>
  </div>
</template>

<script>
  import _ from 'lodash'
  import {Table, TableColumn, Select, Option} from 'element-ui'
  import PPagination from 'src/components/UIComponents/Pagination.vue'

  export default {
    props: {
      classes: String,
      tableData: {
        type: Array,
        description: 'Table data',
        required: true,
        default: () => []
      },
      remoteUrl: {
        type: String,
        description: 'Remote API Url',
        default: () => null
      },
      saveState: {
        type: Boolean,
        description: 'Save state in session storage',
        default: () => false
      },
      saveStateKey: {
        type: String,
        description: 'Save state storage key prefix',
        default: function () {
          return `paginated_table__${this.remoteUrl}`
        }
      },
      defaultOrdering: {
        type: String,
        description: 'Default ordering',
        default: () => null
      },
      perPage: {
        type: Number,
        description: 'Default pagination size',
        default: () => 5
      },
      searchFields: {
        type: Array,
        description: 'Search column fields',
        default: () => []
      },
      tableColumns: {
        type: Array,
        description: 'Table columns info',
        default: () => [
          {
            prop: 'name',
            label: 'Name',
            minWidth: 200
          },
          {
            prop: 'email',
            label: 'Email',
            minWidth: 250
          },
          {
            prop: 'age',
            label: 'Age',
            minWidth: 100
          },
          {
            prop: 'salary',
            label: 'Salary',
            minWidth: 120
          }
        ]
      }
    },
    components: {
      [Select.name]: Select,
      [Option.name]: Option,
      [Table.name]: Table,
      [TableColumn.name]: TableColumn,
      PPagination
    },
    name: 'PaginatedTable',
    computed: {
      searchEnabled () {
        return this.searchFields.length > 0
      },
      pagedData () {
        return this.tableData.slice(this.from, this.to)
      },
      /***
       * Searches through table data and returns a paginated array.
       * Note that this should not be used for table with a lot of data as it might be slow!
       * Do the search and the pagination on the server and display the data retrieved from server instead.
       * @returns {computed.pagedData}
       */
      queriedData () {
        if (!this.remoteUrl) {
          if (!this.searchQuery) {
            this.pagination.total = this.tableData.length
            return this.pagedData
          }
          let result = this.tableData
            .filter((row) => {
              let isIncluded = false
              for (let key of this.searchFields) {
                let value = null
                key.split('.').forEach((crumb) => {
                  if (value === null) {
                    value = row[crumb]
                  } else {
                    value = value[crumb]
                  }
                })
                let rowValue = (value || '').toString().toLowerCase()
                if (rowValue.includes && rowValue.includes(this.searchQuery.toLowerCase())) {
                  isIncluded = true
                }
              }
              return isIncluded
            })
          this.pagination.total = result.length
          return result.slice(this.from, this.to)
        } else {
          return this.remoteData
        }
      },
      to () {
        let highBound = this.from + this.pagination.perPage
        if (this.total < highBound) {
          highBound = this.total
        }
        return highBound
      },
      from () {
        return this.pagination.perPage * (this.pagination.currentPage - 1)
      },
      total () {
        if (this.isRemote) {
          return this.pagination.total
        }
        else {
          this.pagination.total = this.tableData.length
          return this.tableData.length
        }

      }
    },
    beforeDestroy () {
      // Сохранение состояния в sessionStorage, если указан параметр saveState
      // debugger
      if (this.saveState) {
        sessionStorage.setItem(`${this.saveStateKey}_perPage`, this.pagination.perPage)
        sessionStorage.setItem(`${this.saveStateKey}_remoteOrdering`, this.remoteOrdering)
        sessionStorage.setItem(`${this.saveStateKey}_remoteFilters`, JSON.stringify(this.remoteFilters))
      }
    },
    created () {
      if (this.saveState) {
        this.pagination.perPage = parseInt(sessionStorage.getItem(`${this.saveStateKey}_perPage`)) || this.pagination.perPage
        this.remoteOrdering = sessionStorage.getItem(`${this.saveStateKey}_remoteOrdering`) || null
        this.remoteFilters = sessionStorage.getItem(`${this.saveStateKey}_remoteFilters`) || null
        if (this.remoteFilters) {
          this.remoteFilters = JSON.parse(this.remoteFilters)
        }
      }

      if (this.remoteUrl) {
        this.isRemote = true
        this.fetchData()
      } else {
        this.isRemote = false
      }
    },
    mounted () {
      if (this.remoteOrdering) {
        this.remoteOrdering.split(',').forEach((e) => {
          let order =  e.startsWith('-') ? "descending" : "ascending"
          if (e.startsWith('-')) {e = e.replace('-', '')}
          let column = this.$refs.elTable.columns.find(function (c) {return c.sortBy === e})
          if (column) {
            column.order = order
            this.$refs.elTable.store.states.sortingColumn = column
            this.$refs.elTable.store.states.sortOrder = order
          }
        })
      }

      if (this.remoteFilters) {
        this.remoteFilters.forEach((e) => {
          let column = this.$refs.elTable.columns.find(function (c) {return c.sortBy === e.key})
          if (column) {
            column.filteredValue = e.value
          }
        })
      }
    },
    data () {
      return {
        pagination: {
          perPage: this.perPage,
          currentPage: 1,
          perPageOptions: [5, 10, 25, 50],
          total: 0
        },
        searchQuery: '',
        remoteData: null,
        remoteLoading: false,
        remoteOrdering: this.defaultOrdering || null,
        remoteFilters: null
      }
    },
    methods: {
      debounceSearchQuery: _.debounce(function (value) {
        this.searchQuery = value
      }, 500),
      fetchData () {
        this.remoteLoading = true
        const params = {limit: this.pagination.perPage, offset: this.from}
        if (this.remoteOrdering) {
          params.ordering = this.remoteOrdering
        }
        if (this.searchQuery !== '') {
          params.search = this.searchQuery
        }

        if (this.remoteFilters) {
          this.remoteFilters.forEach(function (f) {
            params[f.key] = f.value.join(',')
          })
        }

        this.axios.get(this.remoteUrl, {params}).then(r => {
          this.remoteData = r.data.results
          this.pagination.total = r.data.count
          this.remoteLoading = false
        }).catch(() => {
          this.remoteLoading = false
        })
      },
      sortChange ({column, prop, order}) {
        if (order) {
          this.remoteOrdering = `${order === 'descending' ? '-': ''}${column.sortBy}`
        } else {
          this.remoteOrdering = null
        }
        this.fetchData()
      },
      emitEventHandler(event) {
        this.$emit(event, ...Array.from(arguments).slice(1))
      },
      filterHandler (filters) {
        if (this.isRemote) {
          this.remoteFilters = this.$refs.elTable.columns.filter((e) => {return e.filteredValue && e.filteredValue.length > 0}).map(function (f) {
            return {
              key: f.sortBy,
              value: f.filteredValue
            }
          })
          this.fetchData()
        }
        this.$emit('filter-change', filters)
      }
    },
    watch: {
      'pagination.currentPage': function () {
        this.fetchData()
      },
      'pagination.perPage': function () {
        this.fetchData()
      },
      'remoteUrl': function () {
        this.fetchData()
      },
      'searchQuery': function () {
        this.fetchData()
      }
    }
  }
</script>

<style scoped>
  .paginated-table-buttons .btn {
    margin-bottom: 0;
    margin-top: 0;
  }
</style>
